import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { catalogs, type ar } from "./messages";
import { DEFAULT_LOCALE, getLocaleDirection, type Locale } from "./config";

type Catalog = typeof ar;

/**
 * A deliberately small i18n runtime.
 *
 * `apps/seller` has a bespoke one over a ~217 KB flat key file, and there is no
 * shared i18n package to reuse (that would be its own initiative). This app
 * needs a fraction of that surface, so it gets a typed nested catalog instead:
 * `t((m) => m.pos.close)` is checked by the compiler, which means a missing or
 * renamed key is a build error rather than a key string rendered on a till in
 * the middle of service.
 */
export type TranslateFn = <T extends string>(
  select: (catalog: Catalog) => T,
  values?: Record<string, string | number>,
) => string;

type I18nValue = {
  locale: Locale;
  direction: "rtl" | "ltr";
  t: TranslateFn;
};

const I18nContext = createContext<I18nValue | null>(null);

/** Replaces `{name}` placeholders. Absent values are left visible, not blanked. */
function interpolate(
  template: string,
  values?: Record<string, string | number>,
): string {
  if (!values) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * A translator for a language other than the screen's — the customer papers
 * print in the store's receipt language (owner, 2026-10-08) while the till
 * stays in the cashier's.
 */
export function translatorFor(locale: Locale): TranslateFn {
  return (select, values) => interpolate(select(catalogs[locale]), values);
}

export function I18nProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const t = useCallback<TranslateFn>(
    (select, values) => interpolate(select(catalogs[locale]), values),
    [locale],
  );

  const value = useMemo<I18nValue>(
    () => ({ locale, direction: getLocaleDirection(locale), t }),
    [locale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    // Failing loudly beats silently rendering Arabic to an English user, or
    // worse, crashing mid-service on an undefined catalog.
    return {
      locale: DEFAULT_LOCALE,
      direction: getLocaleDirection(DEFAULT_LOCALE),
      t: (select, values) =>
        interpolate(select(catalogs[DEFAULT_LOCALE]), values),
    };
  }
  return value;
}

export function useTranslate(): TranslateFn {
  return useI18n().t;
}
